import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, Observable, of, tap } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { ToastrService } from "ngx-toastr";
import { TranslocoService } from "@ngneat/transloco";
import { TicketInterface } from "../interfaces/ticket-interface";

@Injectable({
    providedIn: "root",
})
export class TicketService {
    private _tickets: BehaviorSubject<TicketInterface[]> = new BehaviorSubject<any[]>([]);
    private _ticket: BehaviorSubject<TicketInterface | null> = new BehaviorSubject(null);
    private _conversations = new Map<string, any[]>(); // Mapa para almacenar conversaciones por ticket ID

    constructor(
        private _httpClient: HttpClient,
        private _toastr: ToastrService,
        private _transloco: TranslocoService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Observable para la lista de tickets
     */
    get tickets$(): Observable<TicketInterface[]> {
        return this._tickets.asObservable();
    }

    /**
     * Observable para un ticket específico
     */
    get ticket$(): Observable<TicketInterface | null> {
        return this._ticket.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Listar todos los tickets
     */
    list(): Observable<any[]> {
        return this._httpClient.get<any[]>(`/api/v1/ticket`).pipe(
            tap((tickets) => this._tickets.next(tickets)),
            catchError((error) => this._handleError(error, "tickets.error_list")),
        );
    }

    /**
     * Obtener un ticket por ID
     */
    show(id: string): Observable<TicketInterface> {
        return this._httpClient.get<any>(`/api/v1/ticket/${id}`).pipe(
            tap((ticket: TicketInterface) => this._ticket.next(ticket)),
            catchError((error) => this._handleError(error, "tickets.error_show")),
        );
    }
    
    create(ticket: TicketInterface): Observable<TicketInterface> {
        const params = this._formatParams(ticket);

        return this._httpClient.post<any>(`/api/v1/ticket`, params).pipe(
            tap(() => this.list().subscribe()),
            catchError((error) => this._handleError(error, "tickets.error_create")),
        );
    }

    update(id: string, ticket: any): Observable<any> {
        const params = this._formatParams(ticket);
        return this._httpClient
            .put<any>(`/api/v1/ticket/${id}`, params)
            .pipe(catchError((error) => this._handleError(error, "tickets.error_update")));
    }

    delete(id: string): Observable<any> {
        return this._httpClient.delete<any>(`/api/v1/ticket/${id}`).pipe(
            tap(() => this.list().subscribe()),
            catchError((error) => this._handleError(error, "tickets.error_delete")),
        );
    }

    private _formatParams(data: any): { [key: string]: any } | FormData {
        const hasFile: boolean = Object.values(data).some((value) => value instanceof File);

        if (hasFile) {
            const formData: FormData = new FormData();

            Object.keys(data).forEach((key: string) => {
                const value = data[key];
                if (value instanceof File) {
                    formData.append(key, value, value.name);
                } else if (Array.isArray(value)) {
                    value.forEach((item, index) => {
                        formData.append(`${key}[${index}]`, String(item));
                    });
                } else if (typeof value === "object" && value !== null) {
                        formData.append(key, JSON.stringify(value));
                } else {
                    formData.append(key, String(value));
                }
            });

            return formData;
        }

        return data;
    }
    /**
     * Manejar errores y mostrar notificaciones
     */
    private _handleError(error: any, customMessageKey: string): Observable<any> {
        const errorMessage = error?.error?.message || this._transloco.translate("tickets.unknown_error");
        const customMessage = this._transloco.translate(customMessageKey);
        this._toastr.error(`${customMessage}: ${errorMessage}`);
        return of(null);
    }
}
